import { useEffect, useState } from "react";

export const HELP_LAUNCHER_SELECTOR = "#lighthouse iframe#Launcher";

export type HelpLauncherBox = { left: number; bottom: number; height: number };

const measureLauncher = (): HelpLauncherBox | null => {
  const el = document.querySelector<HTMLElement>(HELP_LAUNCHER_SELECTOR);
  if (!el || getComputedStyle(el).visibility === "hidden") {
    return null;
  }
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return null;
  }
  return { left: rect.left, bottom: rect.bottom, height: rect.height };
};

const sameBox = (a: HelpLauncherBox | null, b: HelpLauncherBox | null): boolean =>
  a === b ||
  (a !== null && b !== null && a.left === b.left && a.bottom === b.bottom && a.height === b.height);

export const useHelpLauncherBox = (): HelpLauncherBox | null => {
  const [box, setBox] = useState<HelpLauncherBox | null>(() => measureLauncher());

  useEffect(() => {
    let frame: number | null = null;
    const update = () => {
      if (frame !== null) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = null;
        const next = measureLauncher();
        setBox((previous) => (sameBox(previous, next) ? previous : next));
      });
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
    };
  }, []);

  return box;
};
